/**
 * Standalone screen reached when a guest opens an invite link
 * (`?inviteCode=ABCD` or `?groupCartId=...`).
 *
 * On mount we probe the cart by id (if present) so we can render the host's
 * name + participant count before the user types theirs in. If only an
 * invite code was supplied we skip the probe and call joinByCode directly
 * after the user submits a display name.
 *
 * Post-join we navigate to /home so HomeScreen renders with GroupBanner.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { groupCartApi, type ActiveCartProbe } from "@/api/group-cart";
import { ApiError } from "@/api/http";
import { GroupIllo } from "@/components/group/GroupIllo";
import { useGroupCart } from "@/store/group-cart";
import { useTable } from "@/store/table";

type Phase = "loading" | "ready" | "submitting" | "full" | "ended" | "error";

export function GroupJoinScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { tableInfo } = useTable();
  const { joinByCode, joinById, status, cart } = useGroupCart();

  const inviteCode = params.get("inviteCode")?.toUpperCase().trim() ?? null;
  const groupCartId = params.get("groupCartId") ?? params.get("id") ?? null;

  const [phase, setPhase] = useState<Phase>("loading");
  const [name, setName] = useState("");
  const [code, setCode] = useState(inviteCode ?? "");
  const [probe, setProbe] = useState<ActiveCartProbe | null>(null);
  const [error, setError] = useState<string | null>(null);

  // If we already have an active cart in store with the same id, just bounce home.
  useEffect(() => {
    if (cart && (cart.groupCartId === groupCartId || cart.inviteCode === inviteCode)) {
      navigate("/home", { replace: true });
    }
  }, [cart, groupCartId, inviteCode, navigate]);

  // Probe by branch+table when we have one — gives us host name to render.
  useEffect(() => {
    if (!tableInfo?.branchId || !tableInfo.tableId) {
      setPhase("ready");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await groupCartApi.probeActive(
          tableInfo.branchId!,
          tableInfo.tableId,
        );
        if (cancelled) return;
        setProbe(result);
        setPhase(result?.status === "active" ? "ready" : result ? "ended" : "ready");
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tableInfo?.branchId, tableInfo?.tableId]);

  const canSubmit = useMemo(() => {
    if (phase === "submitting") return false;
    if (!name.trim()) return false;
    if (!groupCartId && code.trim().length !== 4) return false;
    return true;
  }, [phase, name, code, groupCartId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setPhase("submitting");
    setError(null);
    try {
      if (groupCartId) {
        await joinById(groupCartId, { displayName: name.trim() });
      } else {
        await joinByCode(code.trim().toUpperCase(), {
          displayName: name.trim(),
        });
      }
      navigate("/home", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setPhase("full");
          return;
        }
        if (err.status === 410 || err.status === 404) {
          setPhase("ended");
          return;
        }
      }
      setError((err as Error).message ?? "Failed to join");
      setPhase("ready");
    }
  };

  if (phase === "loading" && !probe && (groupCartId || inviteCode)) {
    return (
      <div style={pageStyle}>
        <div style={{ color: "var(--ink-3)" }}>…</div>
      </div>
    );
  }

  if (phase === "full") {
    return (
      <ErrorState
        title={t("v2.group_cart.join_full_title")}
        sub={t("v2.group_cart.join_full_sub")}
        onBack={() => navigate("/home")}
      />
    );
  }
  if (phase === "ended") {
    return (
      <ErrorState
        title={t("v2.group_cart.join_ended_title")}
        sub={t("v2.group_cart.join_ended_sub")}
        onBack={() => navigate("/home")}
      />
    );
  }

  return (
    <div style={pageStyle}>
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%" }}
      >
        <div style={{ alignSelf: "center" }}>
          <GroupIllo size={160} />
        </div>

        <div style={{ textAlign: "center" }}>
          <div className="display" style={{ fontSize: 22, fontWeight: 800, color: "var(--ink-1)" }}>
            {probe?.hostDisplayName
              ? t("v2.group_cart.join_host_label", { name: probe.hostDisplayName })
              : t("v2.group_cart.start_title")}
          </div>
          {probe && (
            <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4 }}>
              {t("v2.group_cart.people_count", {
                count: probe.participantCount,
                defaultValue: `${probe.participantCount} people`,
              })}
            </div>
          )}
        </div>

        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>{t("v2.group_cart.your_name")}</span>
          <input
            data-testid="group-join-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("v2.group_cart.your_name_placeholder")}
            maxLength={32}
            autoFocus
            style={inputStyle}
          />
        </label>

        {!groupCartId && (
          <label style={fieldStyle}>
            <span style={fieldLabelStyle}>
              {t("v2.group_cart.invite_code_label")}
            </span>
            <input
              data-testid="group-join-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 4))}
              placeholder={t("v2.group_cart.invite_code_placeholder")}
              inputMode="text"
              autoCapitalize="characters"
              maxLength={4}
              style={{
                ...inputStyle,
                fontSize: 22,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                letterSpacing: 6,
                textAlign: "center",
                textTransform: "uppercase",
                height: 48,
              }}
            />
          </label>
        )}

        {error && (
          <div
            role="alert"
            style={{
              fontSize: 12,
              color: "#b91c1c",
              background: "#fee2e2",
              padding: "8px 12px",
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}

        <button
          data-testid="group-join-submit"
          type="submit"
          disabled={!canSubmit}
          style={{
            width: "100%",
            height: 48,
            borderRadius: 12,
            background: canSubmit
              ? "var(--primary-600)"
              : "var(--surface-2-strong, #e5e7eb)",
            color: "#fff",
            border: "none",
            fontSize: 15,
            fontWeight: 700,
            cursor: phase === "submitting" ? "wait" : "pointer",
          }}
        >
          {phase === "submitting" || status === "joining"
            ? t("v2.group_cart.join_loading")
            : t("v2.group_cart.join_cta")}
        </button>
      </form>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  padding: "24px 18px",
  minHeight: "100%",
  background: "var(--surface-1)",
};

const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: "var(--ink-2)",
  letterSpacing: 0.2,
};

const inputStyle: React.CSSProperties = {
  height: 44,
  padding: "0 14px",
  borderRadius: 12,
  border: "1px solid var(--border)",
  background: "var(--surface-1)",
  fontSize: 15,
  outline: "none",
};

function ErrorState({
  title,
  sub,
  onBack,
}: {
  title: string;
  sub: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ ...pageStyle, alignItems: "center", textAlign: "center" }}>
      <GroupIllo size={140} />
      <div className="display"
        style={{
          fontSize: 20,
          fontWeight: 800,
          color: "var(--ink-1)",
          marginTop: 16,
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 6 }}>{sub}</div>
      <button
        onClick={onBack}
        style={{
          marginTop: 20,
          padding: "10px 18px",
          background: "var(--ink-1)",
          color: "#fff",
          borderRadius: "var(--radius-card)",
          border: "none",
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        {t("v2.group_cart.back")}
      </button>
    </div>
  );
}
